import { z } from 'zod';
import { WebhookSchemas, WebhookResponseSchemas } from '../domain/webhooks';

/**
 * API contracts for webhook endpoints
 * Grouped by route collection for better organization
 */

/**
 * Request schemas for webhook endpoints
 */
export const WebhookRequestSchemas = {
  // Stripe webhook - raw text body with signature header
  stripeWebhook: z.object({
    body: z.string(), // Raw Stripe webhook body
    headers: z.object({
      'stripe-signature': z.string(),
    }),
  }),

  // WhatsApp webhook verification (GET request)
  whatsappVerification: z.object({
    'hub.mode': z.literal('subscribe'),
    'hub.verify_token': z.string(),
    'hub.challenge': z.string(),
  }),

  // WhatsApp webhook payload (POST request)
  whatsappWebhook: WebhookSchemas.whatsappWebhook,

  // WhatsApp webhook with signature header
  whatsappWithSignature: z.object({
    body: WebhookSchemas.whatsappWebhook,
    headers: z.object({
      'x-hub-signature-256': z.string(),
    }),
  }),
};

/**
 * Webhooks API collection - all /webhooks routes
 */
export const WebhooksAPI = {
  /**
   * POST /webhooks/stripe - Stripe webhook endpoint
   * Processes Stripe events (subscription changes, payments, etc.)
   */
  stripe: {
    request: z.object({
      // Note: Stripe webhooks require raw body validation,
      // so we define a minimal schema here
    }),
    response: WebhookResponseSchemas.acknowledgment,
    errorResponse: WebhookResponseSchemas.error,
  },

  /**
   * GET /webhooks/whatsapp - WhatsApp webhook verification
   * Verifies webhook subscription with Meta Business API
   */
  whatsappVerification: {
    request: z.object({
      'hub.mode': z.literal('subscribe'),
      'hub.verify_token': z.string(),
      'hub.challenge': z.string(),
    }),
    response: z.string(), // Returns the challenge string directly
    errorResponse: z.string(), // Returns error message as text
  },

  /**
   * POST /webhooks/whatsapp - WhatsApp webhook events
   * Processes WhatsApp messages and status updates
   */
  whatsappWebhook: {
    request: WebhookSchemas.whatsappWebhook,
    response: WebhookResponseSchemas.acknowledgment,
    errorResponse: WebhookResponseSchemas.error,
  },
};

// Export request schema types
export type StripeWebhookRequest = z.infer<
  typeof WebhookRequestSchemas.stripeWebhook
>;
export type WhatsAppApiVerificationRequest = z.infer<
  typeof WebhookRequestSchemas.whatsappVerification
>;
export type WhatsAppApiWebhookRequest = z.infer<
  typeof WebhookRequestSchemas.whatsappWebhook
>;
export type WhatsAppWithSignatureRequest = z.infer<
  typeof WebhookRequestSchemas.whatsappWithSignature
>;

// Export response types (from domain schemas)
export type WebhookAcknowledgmentResponse = z.infer<
  typeof WebhookResponseSchemas.acknowledgment
>;
export type WebhookVerificationResponse = z.infer<
  typeof WebhookResponseSchemas.verification
>;
export type WebhookErrorResponse = z.infer<typeof WebhookResponseSchemas.error>;

// Export API types (for convenience)
export type WebhooksApiStripeRequest = z.infer<
  typeof WebhooksAPI.stripe.request
>;
export type WebhooksApiStripeResponse = z.infer<
  typeof WebhooksAPI.stripe.response
>;
export type WebhooksApiWhatsAppVerificationRequest = z.infer<
  typeof WebhooksAPI.whatsappVerification.request
>;
export type WebhooksApiWhatsAppVerificationResponse = z.infer<
  typeof WebhooksAPI.whatsappVerification.response
>;
export type WebhooksApiWhatsAppWebhookRequest = z.infer<
  typeof WebhooksAPI.whatsappWebhook.request
>;
export type WebhooksApiWhatsAppWebhookResponse = z.infer<
  typeof WebhooksAPI.whatsappWebhook.response
>;
